import { Fragment, ReactElement, useId, useState } from 'react'
import { formatCents, formatShare } from '../format'
import * as S from './styles'

export interface PaymentSummaryRowData {
  key: string
  label: string
  quantity: number
  revenueCents: number
  revenueShare: number | null
  feeCents?: number
  details?: PaymentSummaryRowData[]
}

export interface PaymentSummaryData {
  title?: string
  showFee: boolean
  rows: PaymentSummaryRowData[]
  total: { quantity: number, revenueCents: number, feeCents?: number }
}

const count = (value: number): string => value.toLocaleString('pt-BR')

interface PaymentSummaryProps {
  data: PaymentSummaryData
  expandAll?: boolean
}

export function PaymentSummary({ data, expandAll = false }: PaymentSummaryProps): ReactElement {
  const titleId = useId()
  const baseId = useId()
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const toggle = (key: string): void => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  return (
    <S.Wrapper aria-labelledby={titleId}>
      <S.Title id={titleId}>{data.title ?? 'Resumo por forma de pagamento'}</S.Title>
      <S.Scroll>
        <S.Table>
          <thead>
            <tr>
              <S.HeadCell scope="col">Forma de pagamento</S.HeadCell>
              <S.HeadCell scope="col" $numeric>Ingressos</S.HeadCell>
              <S.HeadCell scope="col" $numeric>Receita</S.HeadCell>
              <S.HeadCell scope="col" $numeric>% da receita</S.HeadCell>
              {data.showFee && <S.HeadCell scope="col" $numeric>Taxa</S.HeadCell>}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const details = row.details ?? []
              const open = expandAll || expanded.has(row.key)
              const detailIds = details.map((detail) => `${baseId}-${detail.key}`)
              return (
                <Fragment key={row.key}>
                  <tr>
                    <S.RowHead scope="row">
                      {details.length === 0 ? (
                        row.label
                      ) : (
                        <S.Toggle type="button" aria-expanded={open} aria-controls={detailIds.join(' ')} onClick={() => toggle(row.key)}>
                          <S.Chevron aria-hidden="true" $open={open}>›</S.Chevron>
                          <span>{row.label}</span>
                        </S.Toggle>
                      )}
                    </S.RowHead>
                    <S.Cell $numeric>{count(row.quantity)}</S.Cell>
                    <S.Cell $numeric>{formatCents(row.revenueCents)}</S.Cell>
                    <S.Cell $numeric>{row.revenueShare === null ? '—' : formatShare(row.revenueShare)}</S.Cell>
                    {data.showFee && <S.Cell $numeric>{formatCents(row.feeCents ?? 0)}</S.Cell>}
                  </tr>
                  {details.map((detail, index) => (
                    <S.SubRow key={detail.key} id={detailIds[index]} hidden={!open}>
                      <S.RowHead scope="row">{detail.label}</S.RowHead>
                      <S.Cell $numeric>{count(detail.quantity)}</S.Cell>
                      <S.Cell $numeric>{formatCents(detail.revenueCents)}</S.Cell>
                      <S.Cell $numeric>{detail.revenueShare === null ? '—' : formatShare(detail.revenueShare)}</S.Cell>
                      {data.showFee && <S.Cell $numeric>{formatCents(detail.feeCents ?? 0)}</S.Cell>}
                    </S.SubRow>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
          <tfoot>
            <S.TotalRow>
              <S.RowHead scope="row">Total</S.RowHead>
              <S.Cell $numeric>{count(data.total.quantity)}</S.Cell>
              <S.Cell $numeric>{formatCents(data.total.revenueCents)}</S.Cell>
              <S.Cell $numeric>{data.total.revenueCents > 0 ? formatShare(100) : '—'}</S.Cell>
              {data.showFee && <S.Cell $numeric>{formatCents(data.total.feeCents ?? 0)}</S.Cell>}
            </S.TotalRow>
          </tfoot>
        </S.Table>
      </S.Scroll>
    </S.Wrapper>
  )
}
